import escapeRegExp from "escape-string-regexp";

export const getUserContactIdForOrg = (user, org) => {
  if (!user.affiliations.length) return null;

  const correctAffiliation = user.affiliations.find((a) => a.organization.id === org.id);

  return correctAffiliation.contact_id.toString() || null;
};

const countCompleteTasks = (tasks) => {
  if (!tasks || !tasks.length) return 0;

  return tasks.reduce((accumulator, task) => {
    if (task.completed_at) return accumulator + 1;
    else return accumulator;
  }, 0);
};

export const countCompleteSteps = (steps) =>
  steps.reduce((accum, step) => {
    const completeTasks = countCompleteTasks(step.tasks);
    return accum + completeTasks;
  }, 0);

export const countNumberOfTasksInSteps = (steps) => {
  if (!steps) return 0;

  return steps.reduce((accum, step) => {
    const n = step.tasks ? step.tasks.length : 0;
    return accum + n;
  }, 0);
};

// returns DECIMAL. 1.00 === 100%. 0.05 === 5%, etc
export const percentOfStepTasksComplete = (steps) => {
  if (!steps) return 0;

  const completeSteps = countCompleteSteps(steps);

  const totalTasks = countNumberOfTasksInSteps(steps);
  if (totalTasks === 0) return 0;

  return completeSteps / totalTasks;
};

// returns DECIMAL. 1.00 === 100%. 0.05 === 5%, etc
export const percentOfTasksComplete = (tasks) => {
  if (!tasks.length) return 0;
  return countCompleteTasks(tasks) / tasks.length;
};

export const getRoleWithYear = (contact = {}) => {
  const { constituencies = [] } = contact;
  let roles;

  if (constituencies.length) {
    const statuses = constituencies.map((c) => c.status).filter((s) => !!s);
    if (statuses.length) {
      roles = statuses.join(", ").replace(";", "");
    }
  }

  const year = getYear(contact);

  if (roles && year) return `${roles} - ${year}`;
  else if (roles) return roles;
  else if (year) return year;
  else return "";
};

export const modifyJourneyTaskCounts = (journey, completedAt, actionType) => {
  const { tasks_due_counts = {} } = journey;
  let { completed, total } = tasks_due_counts;

  let type;
  switch (actionType) {
    case "EMAIL":
      type = "email";
      break;
    case "PHONE":
      type = "phone";
      break;
    default:
      type = "other";
      break;
  }

  if (completedAt) {
    completed += 1;
    tasks_due_counts[type] -= 1;
    total -= 1;
  } else {
    completed -= 1;
    tasks_due_counts[type] += 1;
    total += 1;
  }

  journey.tasks_due_counts = { ...tasks_due_counts, completed, total };
};

const getYear = (contact) => {
  const { contact_attributes = {} } = contact;

  if (contact_attributes) {
    const { year } = contact_attributes;
    if (year) return `Class of ${year}`;
  }

  return null;
};

export const addMillisecondsToTimestamp = (timestamp) => {
  return Math.floor(timestamp * 1000);
};

export const removeMillisecondsToTimestamp = (timestamp) => {
  return Math.floor(timestamp / 1000);
};

export const formatDateFromTimestampAndAddMilliseconds = (timestamp, addDay = false) => {
  const date = new Date(timestamp * 1000);
  let options = { year: "numeric", month: "long", day: "numeric" };
  if (addDay) {
    date.setDate(date.getDate() + 1);
    options.weekday = "long";
  }
  return date.toLocaleDateString("en-US", options);
};

export const formatDateFromTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const options = { year: "numeric", month: "long", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

export const formatDateFromTimestampWeekday = (timestamp) => {
  const date = new Date(timestamp);

  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  return date.toLocaleDateString("en-US", options);
};

export const setTimestampToMidnight = (timestamp) => {
  let newDate = new Date(timestamp); // create date object
  newDate.setHours(0, 0, 0, 0); // set times to midnight

  return newDate;
};

export const setTimestampToMidnightAndRemoveMilliseconds = (timestamp) => {
  const midnightTimestamp = setTimestampToMidnight(timestamp);

  return removeMillisecondsToTimestamp(midnightTimestamp);
};

// Takes in a date in milliseconds and returns in the format "Mon, Aug 2"
export const formatSingleDate = (dateInMilliseconds) => {
  const timestamp = Number(dateInMilliseconds);
  const date = new Date(timestamp);

  const formattedDate = date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return formattedDate;
};

export const updateTasksAfterTimeout = (dataArray, startDate, endDate) => {
  const isDateInRange = (date, start, end) => {
    return date >= start && date <= end;
  };

  return dataArray.map((obj) => {
    const objDate = new Date(obj.date);
    if (isDateInRange(objDate, new Date(startDate * 1000), new Date(endDate * 1000))) {
      return {
        ...obj,
        email: 0,
        phone: 0,
        other: 0,
        total: 0,
        completed: 0,
      };
    }
    return obj;
  });
};
export const countTasksForJourneys = (journeys, fieldsToInclude = null) => {
  const totalsObj = {};
  journeys.forEach(({ tasks_due_counts = {} }) => {
    Object.entries(tasks_due_counts).forEach(([key, val]) => {
      if (totalsObj[key]) totalsObj[key] += val;
      else totalsObj[key] = val;
    });
  });

  if (fieldsToInclude) {
    const filteredTotals = {};
    fieldsToInclude.forEach((field) => {
      if (field === "notEmailOrPhone") {
        // ugly
        filteredTotals["other"] = totalsObj["other"];
      } else {
        filteredTotals[field] = totalsObj[field];
      }
    });

    return filteredTotals;
  }
  return totalsObj;
};
export const filterOnSearch = (itemsArray, searchInput) => {
  const sanitizedSearchInput = searchInput.replace(/[()"']/g, ""); // Remove () and double quotes
  const searchTerms = sanitizedSearchInput.split(/\s+/); // Split sanitized search input into searchInput

  return itemsArray.filter((item) => {
    const { properties = {} } = item;

    const propertiesToSearch = ["name_first", "name_nick", "name_maiden", "name_last"];

    // Create the formatted name
    const formattedName = propertiesToSearch
      .map(function (property) {
        const value = properties[property] ? properties[property].value.replace(/[()"']/g, "") : "";
        return property === "name_nick" ? `"${value}"` : value;
      })
      .filter((p) => !!p)
      .join(" ");

    // Check if search input has spaces
    if (sanitizedSearchInput.includes(" ")) {
      // For searches with spaces, check if each term is present in the formatted name

      return searchTerms.every((term) => new RegExp(escapeRegExp(term), "i").test(formattedName));
    } else {
      // For searches without spaces, check if the search input is present in any property
      return propertiesToSearch.some((property) => {
        const value = properties[property] ? properties[property].value.replace(/[()"']/g, "") : "";
        return new RegExp(escapeRegExp(sanitizedSearchInput), "i").test(value);
      });
    }
  });
};

export const formatGivingDate = (dateString) => {
  const date = new Date(dateString + "T00:00");

  if (isNaN(date)) {
    return;
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const givingTimeAgo = (pastDate) => {
  const currentDate = new Date();
  const past = new Date(pastDate);
  const diffInSeconds = Math.floor((currentDate - past) / 1000);

  if (diffInSeconds < 86400) {
    return `(Less than a day ago)`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `(${days} day${days > 1 ? "s" : ""} ago)`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `(${months} month${months > 1 ? "s" : ""} ago)`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `(${years} year${years > 1 ? "s" : ""} ago)`;
  }
};
